import React from 'react';

const faqs = [
  {
    question: 'What is it like to care for a young person in residential care?',
    answer: (
      <>
        Caring for a young person in residential care is similar to parenting, with the added responsibility of
        supporting someone who may have experienced trauma or disruption early in life. The role involves creating
        consistency through clear boundaries and predictable routines, while also allowing room for enjoyment, creativity,
        and exploration. You encourage young people to develop confidence, discover new interests, and build life skills
        at their own pace.
        <br />
        <br />
        Day to day, the role includes practical responsibilities such as preparing meals, supporting attendance at
        school, accompanying young people to health appointments, and engaging in leisure or extracurricular activities.
        At its core, the purpose is to provide a stable, nurturing, and positive home environment where young people feel
        safe, valued, and able to grow.
      </>
    ),
  },
  {
    question: 'What if I have no experience working with young people who have experienced trauma?',
    answer: (
      <>
        That is absolutely fine. You are not expected to have all the answers from the start. Testa Care provides a
        thorough induction programme alongside ongoing training, ensuring you develop the knowledge and confidence needed
        to support young people effectively. Where a home supports a young person with more complex needs, additional
        tailored training is provided so the team feels well prepared and supported. You will also have regular
        supervision and guidance from your Registered Manager, ensuring you are never without advice or support.
      </>
    ),
  },
  {
    question: 'What do I do while the children are at school?',
    answer: (
      <>
        During school hours, staff focus on preparing and maintaining the home. We follow a "ready by 10 a.m." standard,
        ensuring the environment is clean, organised, and welcoming for professionals such as social workers, education
        partners, and inspectors. This time is also used to complete essential documentation related to the young people,
        the running of the home, and health and safety requirements.
        <br />
        <br />
        In addition, staff manage household tasks such as shopping and stock checks, and may use the time to work toward
        their qualification or attend training sessions. The day remains varied and productive, with plenty to do in
        preparation for the children's return.
      </>
    ),
  },
  {
    question: 'Will my role always be based in the home?',
    answer: (
      <>
        No. While the home is the primary setting, the role often extends beyond it. We actively support young people to
        access enriching life experiences, such as holidays, visits to theme parks, or cultural outings. When a young
        person shows a strong interest or passion, staff work creatively to encourage and support it, whether that means
        accompanying them to a live music event, visiting specialist venues, or supporting health and fitness goals
        through community activities.
      </>
    ),
  },
  {
    question: 'Do you offer graduate roles?',
    answer: (
      <>
        While we don't have a dedicated graduate programme, graduates are successful across many areas of our
        organisation. To explore opportunities that match your skills and interests, you can email{' '}
        <a
          href="mailto:recruitment@testacare.co.uk"
          className="text-[#c46422] underline hover:text-[#8a3c11]"
        >
          recruitment@testacare.co.uk
        </a>{' '}
        or call 07767 054238. Our recruitment team will be happy to guide you toward the right role.
      </>
    ),
  },
  {
    question: 'Do I need to wear a uniform?',
    answer: (
      <>
        No. We want our homes to feel relaxed and welcoming, so staff do not wear uniforms. We simply ask that you wear
        smart, casual clothing that is comfortable and practical for the hands-on nature of residential care work.
      </>
    ),
  },
  {
    question: 'Why are criminal records checks required?',
    answer: (
      <>
        To ensure the safety and well-being of the children in our care, all employment is subject to a Disclosure and
        Barring Service (DBS) check, along with satisfactory references from previous childcare roles. If there is
        anything in your background that could appear on a DBS check, please disclose it during the interview so we can
        assess the circumstances appropriately. Having a criminal record does not automatically prevent employment.
      </>
    ),
  },
  {
    question: 'Can I start immediately?',
    answer: (
      <>
        Working as a Children's Residential Care Worker is a rewarding but demanding role, so we look for individuals who
        align with our values. Our approach follows the PACE model of therapeutic care - Playfulness, Acceptance,
        Curiosity, and Empathy - which focuses on supporting the whole child, not just their behaviour. These principles
        and our ways of working are covered thoroughly during induction, ensuring you feel confident and prepared from
        your first day.
      </>
    ),
  },
];

const FaqSection = () => (
  <section id="faqs" className="relative z-10 -mt-16 md:-mt-24 pb-24 px-4">
    <div className="max-w-5xl mx-auto bg-white border border-[#f4e7da] shadow-[0_25px_65px_rgba(16,24,40,0.08)] rounded-[22px] overflow-hidden">
      <div className="px-6 md:px-12 py-12 md:py-16 space-y-10 text-[#4c4c4c] leading-relaxed">
        <div className="text-center space-y-4">
          <p className="text-xs uppercase tracking-[0.45em] text-[#c46422]">Support</p>
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p>
            We have gathered the answers to the questions we hear most often, so you can understand the Testa Care journey
            before you even apply.
          </p>
        </div>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <article
              key={faq.question}
              className="rounded-[22px] border border-[#eddac9] bg-[#fffdf8] px-6 py-6 shadow-[0_10px_30px_rgba(13,15,16,0.05)]"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <span className="text-[#c46422] font-semibold tracking-[0.4em] uppercase text-sm md:mt-1">Q.</span>
                <div className="space-y-3">
                  <h3 className="text-xl font-light text-[#0d0d0d]">{faq.question}</h3>
                  <p className="text-[#444]">{faq.answer}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default FaqSection;
